import { component as defineComponent } from 'flightjs';
import withChildComponents from 'flight-with-child-components';
import CategoryFilterUI from 'browse/ui/category-filter';
import ToolbarToggleUI from 'browse/ui/toolbar-toggle';
import TextValueFilterUI from 'browse/ui/text-value-filter';
import ToggleValueFilterUI from 'browse/ui/toggle-value-filter';
import StyledSelectFilterUI from 'browse/ui/styled-select-filter';
import DetachedDropdownFilterUI from 'browse/ui/detached-dropdown-filter';
import BroadcastingInputUI from 'contest/ui/broadcasting-input';
import ButtonGroupFilterUI from 'browse/ui/button-group-filter';
import ConditionalOptionsFilterUI from 'browse/ui/conditional-options-filter';
import { debounce } from 'lodash';

export default defineComponent(function () {
    this.attributes({
        'hasLazyCategoryCounts': true,
        'filterCategory': '[data-category-filter]',
        'filterCategoryInput': '[data-category-filter-input]',
        'filterState': '[data-state-filter]',
        'filterStateInput': '[data-state-filter-input]',
        'filterActiveState': '[data-active-state-filter]',
        'filterActiveStateInput': '[data-active-state-filter-input]',
        'filterFinishedState': '[data-finished-state-filter]',
        'filterFinishedStateInput': '[data-finished-state-filter-input]',
        'filterPageInput': '[data-filter-page-input]',
        'filterCheckboxInput': '[data-checkbox-filter]',
        'sortOptionInput': '[data-sort-option-input]',
        'sortDirectionInput': '[data-sort-direction-input]',
        'filterLanguageInput': '[data-language-input]',
        'filterForm': '[data-filter-form]',
        'textValueFilter': '[data-text-value-filter]',
        'toggleValueFilter': '[data-toggle-value-filter]',
        'styledSelectFilter': '[data-styled-select-filter]',
        'detachedDropdownFilter': '[data-detached-dropdown-filter]',
        'buttonGroupFilter': '[data-button-group-filter]',
        'conditionalFilter': '[data-conditional-options-filter]',
        'advancedFiltersToggle': '[data-advanced-filters-toggle]',
        'advancedFiltersBar': '[data-filter-advanced]'
    });

    this.after('initialize', function () {
        this.initChildComponents();
        this.reloadDataWithDebounce = debounce(this.reloadData.bind(this), 100);

        this.on(document, 'uiAdvancedFiltersToggleRequested', this.toggleAdvancedFilters);
        this.on(document, 'uiBrowseFiltersUpdated', this.processFilterChanges);

        this.triggerLoadCategoryCount();
    });

    this.initChildComponents = function () {
        this.attachChild(CategoryFilterUI, this.select('filterCategory'));
        this.attachChild(ToolbarToggleUI, this.select('advancedFiltersToggle'));
        this.attachChild(TextValueFilterUI, this.select('textValueFilter'));
        this.attachChild(ToggleValueFilterUI, this.select('toggleValueFilter'));
        this.attachChild(StyledSelectFilterUI, this.select('styledSelectFilter'));
        this.attachChild(DetachedDropdownFilterUI, this.select('detachedDropdownFilter'));
        this.attachChild(ButtonGroupFilterUI, this.select('buttonGroupFilter'));
        this.attachChild(ConditionalOptionsFilterUI, this.select('conditionalFilter'));
        this.attachChild(BroadcastingInputUI, this.select('filterCheckboxInput'), {
            broadcastEvent: 'uiBrowseFiltersUpdated'
        });
    };

    this.getFilterParameters = function () {
        const dedupedFields = this.select('filterForm').find('input, textarea, select')
            .map(function (idx, item) {
                if ($(item).val() !== '') {
                    return item;
                }
            })
            .serializeArray()
            .reduce((newArr, field) => {
                newArr[field.name] = field;
                return newArr;
            }, []);

        return Object.keys(dedupedFields).map((key) => {
            return dedupedFields[key];
        });
    };

    this.triggerLoadCategoryCount = function () {
        if (this.attr.hasLazyCategoryCounts) {
            this.trigger('uiBrowseLoadCategoryCountRequested', {
                path: this.$node.attr('data-category-counts-path'),
                params: this.getFilterParameters()
            });
        }
    };

    this.triggerLoadContests = function () {
        var data = {
            url: this.select('filterForm').attr('action'),
            ajaxUrl: this.select('filterForm').attr('data-browse-ajax-url'),
            categoryUrl: this.select('filterForm').attr('data-browse-category-url'),
            params: this.getFilterParameters(),
            allowAjax: this.select('filterForm').attr('data-allow-ajax'),
        };

        this.trigger('uiBrowseLoadContestsRequested', data);
    };

    this.toggleAdvancedFilters = function () {
        if (this.select('advancedFiltersBar').is(':visible')) {
            this.select('advancedFiltersBar').attr('data-hidden', true);
            this.trigger('uiBrowseAdvancedFiltersCollapsed');
        } else {
            this.select('advancedFiltersBar').removeAttr('data-hidden');
            this.trigger('uiBrowseAdvancedFiltersExpanded');
        }
    };

    this.processFilterChanges = function (e, data) {
        if (data.hasOwnProperty('category')) {
            this.select('filterCategoryInput').val(data.category);
        }

        if (data.hasOwnProperty('active-state')) {
            this.select('filterActiveStateInput').val(data['active-state']);
        }

        if (data.hasOwnProperty('finished-state')) {
            this.select('filterFinishedStateInput').val(data['finished-state']);
        }

        if (data.hasOwnProperty('language')) {
            this.select('filterLanguageInput').val(data.language);
        }

        this.processPageFilterChange(data);
        this.processSortFilterChange(data);
        this.processStatusFilterChange(data);
        this.processStateFilterChange(data);

        this.reloadDataWithDebounce();
    };

    this.scrollToTop = function() {
        $('body,html').animate({
            scrollTop: this.$node.offset().top
        }, 200);
    };

    this.reloadData = function () {
        this.triggerLoadContests();
        this.triggerLoadCategoryCount();
    };

    this.processPageFilterChange = function (data) {
        if (data.hasOwnProperty('page')) {
            this.scrollToTop();
            this.select('filterPageInput').val(data.page);
        } else {
            this.select('filterPageInput').val(1);
        }
    };

    this.processSortFilterChange = function (data) {
        if (data.hasOwnProperty('sort')) {
            var sortParts = data.sort.split(':');
            if (sortParts.length === 2) {
                this.select('sortOptionInput').val(sortParts[0]);
                this.select('sortDirectionInput').val(sortParts[1]);
            }
        }
    };

    this.processStatusFilterChange = function (data) {
        if (data.hasOwnProperty('status')) {
            if (data.status !== 'open' && this.select('sortOptionInput').val() === 'time-left') {
                this.trigger('uiBrowseFiltersUpdated', {'sort': 'prize:' + this.select('sortDirectionInput').val()});
            }

            if (data.status !== 'open') {
                this.trigger('uiBrowseFiltersUpdated', {
                    'time-remaining': null,
                    'private': null,
                });
            }

            this.trigger('uiBrowseFilterConditionChanged', data);
        }
    };

    this.processStateFilterChange = function (data) {
        if (data.hasOwnProperty('state')) {
            this.select('filterStateInput').val(data.state);

            if (data.state === 'active') {
                this.trigger('uiBrowseFiltersUpdated', {
                    'sort': 'time-left:asc',
                    'active-state': 'all',
                    'finished-state': 'all'
                });

                this.select('filterActiveState').removeAttr('data-hidden');
                this.select('filterFinishedState').attr('data-hidden', true);
            } else if (data.state === 'locked') {
                this.trigger('uiBrowseFiltersUpdated', {
                    'sort': 'start-date:desc',
                    'active-state': 'all',
                    'finished-state': 'all'
                });

                this.select('filterFinishedState').attr('data-hidden', true);
                this.select('filterActiveState').attr('data-hidden', true);
            } else if (data.state === 'finished') {
                this.trigger('uiBrowseFiltersUpdated', {
                    'sort': 'start-date:desc',
                    'active-state': 'all',
                    'finished-state': 'all'
                });

                this.select('filterFinishedState').removeAttr('data-hidden');
                this.select('filterActiveState').attr('data-hidden', true);
            }

            this.trigger('uiBrowseFilterConditionChanged', data);
        }
    };

}, withChildComponents);



// WEBPACK FOOTER //
// ./src/web/js/browse/ui/toolbar.js